import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Divider,
} from "@material-ui/core";
import { Helmet } from "react-helmet-async";
import styled from "styled-components/macro";
import { ROUTES } from "../../constants";

import TimelineIcon from "@material-ui/icons/Timeline";
import GetAppIcon from "@material-ui/icons/GetApp";
import MapIcon from "@material-ui/icons/Map";
import AssessmentIcon from "@material-ui/icons/Assessment";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import {
  ShowChart as ShowChartIcon,
  Equalizer,
  VerticalSplit,
  BubbleChart,
  MultilineChart,
  Timeline,
  TrendingDown,
  WbSunny,
} from "@material-ui/icons";

const theme = {
  primary: "#1A4D8F",
  secondary: "#4CAF50",
  accent: "#FF6B6B",
  text: "#2D3436",
  lightBg: "#F9FBFD",
  divider: "#E0E0E0",
};

const Hero = styled(Box)`
  position: relative;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 3rem;
  color: #fff;
  background-color: #000;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url(/static/img/landing-hero.jpg) center/cover no-repeat;
    filter: brightness(70%);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
    max-width: 1200px;
  }

  h1 {
    line-height: 1.5;
    font-weight: 600;
  }

  @media (max-width: 960px) {
    min-height: 400px;
    padding: 2rem;

    h1 {
      font-size: 2.5rem;
    }
  }
`;

const Section = styled(Box)`
  padding: 6rem 2rem;
  background: ${(p) => p.bg || "#fff"};

  @media (max-width: 960px) {
    padding: 4rem 1rem;
  }
`;

const SectionHeader = ({ title, subtitle }) => (
  <Box mb={8} textAlign="center">
    <Typography
      variant="h2"
      component="h2"
      gutterBottom
      style={{
        fontWeight: 600,
        fontSize: "2.25rem",
        color: theme.primary,
      }}
    >
      {title}
    </Typography>

    {subtitle && (
      <Typography
        variant="body1"
        component="p"
        style={{
          maxWidth: "700px",
          margin: "0 auto 1.5rem",
          color: "#444",
          fontSize: "1.125rem",
        }}
      >
        {subtitle}
      </Typography>
    )}

    <Divider
      style={{
        width: "120px",
        height: "4px",
        backgroundColor: theme.secondary,
        borderRadius: "2px",
        margin: "0 auto",
      }}
    />
  </Box>
);

const AboutBox = styled(Box)`
  max-width: 1000px;
  margin: 0 auto;
  background: white;
  padding: 3rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    background: ${theme.primary};
  }

  @media (max-width: 960px) {
    padding: 2rem;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid ${theme.divider};
  width: 100%;

  .MuiCardContent-root {
    padding: 2rem;
  }

  .MuiCardActions-root {
    padding: 0 2rem 2rem;
  }

  .MuiCardContent-root svg {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    color: ${theme.primary};
  }
`;

const FeatureCard = ({ title, desc, icon, to, external = false }) => {
  const id = `${title.replace(/\s+/g, "-").toLowerCase()}-label`;

  return (
    <StyledCard role="region" aria-labelledby={id}>
      <CardContent style={{ flexGrow: 1, padding: "1.5rem" }}>
        <Box display="flex" alignItems="center" mb={1} aria-hidden="true">
          {icon}
        </Box>
        <Typography variant="h6" component="h3" id={id} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2">{desc}</Typography>
      </CardContent>
      <CardActions style={{ padding: "1.5rem", paddingTop: 0 }}>
        <Button
          variant="contained"
          color="primary"
          href={to}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          fullWidth
          endIcon={external ? <OpenInNewIcon /> : undefined}
          aria-label={
            external ? `Open external link to ${title}` : `Open ${title} tool`
          }
        >
          {external ? "Open Link" : `${title}`}
        </Button>
      </CardActions>
    </StyledCard>
  );
};

const AnnualReportCard = styled(Card)`
  && {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    text-align: center;
    background-color: #e8f5e9;
    border: 2px solid #388e3c;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease, transform 0.2s ease;

    &:hover {
      box-shadow: 0 5px 12px rgba(0, 0, 0, 0.2);
      transform: translateY(-2px);
    }

    a {
      margin-top: 1rem;
    }
  }
`;

export default function LandingPage() {
  const renderCard = (props) => (
    <Grid item xs={12} sm={6} md={4}>
      <Box display="flex" height="100%">
        <FeatureCard {...props} />
      </Box>
    </Grid>
  );

  return (
    <>
      <Helmet title="Cherry Creek Water‑Quality Dashboard" />

      <Hero>
        <div>
          <Typography variant="h1" component="h1" gutterBottom>
            Explore Water Quality <br /> Across the Cherry Creek Basin
          </Typography>
          <Typography variant="h6" paragraph>
            Interactive tools powered by 30 years of water quality data.
          </Typography>
        </div>
      </Hero>

      {/* About Section */}
      <Section bg="#ffffff">
        <SectionHeader title="About the Portal" />
        <AboutBox>
          <Typography variant="body1" paragraph>
            This portal provides access to water quality data and visual tools
            across the Cherry Creek Basin. Visualize trends, compare sites,
            investigate nutrient dynamics, and download curated datasets.
          </Typography>
          <Typography variant="body1" paragraph>
            Data are sourced from long-term monitoring programs managed by the
            Cherry Creek Basin Water Quality Authority (CCBWQA). Our tools are
            designed to support analysis, communication, and water quality
            decision-making across the region.
          </Typography>
          <Typography variant="body2" style={{ color: "#333" }}>
            *Some datasets are provisional and may change after review.
            Questions? Contact{" "}
            <a
              href="mailto:manager@ccbwqa.org"
              style={{ color: "#005ea2", textDecoration: "underline" }}
            >
              manager@ccbwqa.org
            </a>
            .
          </Typography>
        </AboutBox>
      </Section>

      {/* Featured Resources */}
      <Section bg="#f7f9fb">
        <SectionHeader
          title="Featured Resources"
          subtitle="Quick access to the most requested tools and reports."
        />
        <Grid container spacing={5} justify="center">
          <Grid item xs={12} md={8}>
            <AnnualReportCard>
              <Box display="flex" justifyContent="center" mb={2}>
                <OpenInNewIcon
                  fontSize="large"
                  color="primary"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                />
              </Box>
              <Typography variant="h6" component="h3" gutterBottom>
                2025 Annual Report Highlights
              </Typography>
              <Typography variant="body2" color="textPrimary">
                Learn how the Authority and partners are working to preserve
                water quality throughout the basin through the detailed Annual
                Report or the concise Executive Summary.
              </Typography>
              <Box
                mt={2}
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                style={{ gap: "16px", marginBottom: ".5rem" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  href="https://ccbwqportal.org/annual-report/2025/key-takeaways"
                  endIcon={<OpenInNewIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open the 2025 Annual Report Highlights in a new tab"
                  fullWidth
                >
                  Annual Report (Interactive)
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  href="https://ccbwqportal.org/annual-report/2025/key-takeaways#docaccess-eb94e3d03801ae5f388f0ca756d8b6141c8d225d3893f45e04bff6056bc263f8"
                  endIcon={<OpenInNewIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open the 2025 Accessible Version PDF in a new tab"
                  fullWidth
                >
                  Annual Report (Accessible PDF)
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  href="https://ccbwqportal.org/annual-report/2025/key-takeaways#docaccess-81a3183e71eae0f7869e019285e3ea0d2916d5a67b956bbe9c4a085234788136"
                  endIcon={<OpenInNewIcon />}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open the 2025 Executive Summary PDF in a new tab"
                  fullWidth
                >
                  Executive Summary (PDF)
                </Button>
              </Box>
            </AnnualReportCard>
          </Grid>
          {renderCard({
            title: "Interactive Map",
            desc: "Explore spatial data including monitoring sites and PRFs.",
            icon: <MapIcon fontSize="large" />,
            to: ROUTES.MAP,
          })}
        </Grid>
      </Section>

      {/* Water Quality Update Section */}
      <Section bg="#ffffff">
        <SectionHeader
          title="Water Quality Update"
          subtitle="View current conditions and patterns from key inflow sites, seasonal trends, and nutrient dynamics."
        />
        <Grid container spacing={5} justify="center">
          {renderCard({
            title: "Reservoir Water Quality Summary",
            desc: "View long-term seasonal trends and compare with standards.",
            icon: <BubbleChart fontSize="large" />, // ← Matches sidebar
            to: ROUTES.WATER_QUALITY_PARAMETER_VIEWER,
          })}
          {renderCard({
            title: "Reservoir Depth Profile",
            desc: "Examine how water quality changes with depth in the reservoir.",
            icon: <VerticalSplit fontSize="large" />, // ← Matches sidebar
            to: ROUTES.FIELD_DEPTH_PROFILE,
          })}
          {renderCard({
            title: "Nutrient Ratios",
            desc: "Evaluate nutrient limitations using TN:TP, TDN:TDP, and TIN:SRP.",
            icon: <ShowChartIcon fontSize="large" />, // ← Matches sidebar
            to: ROUTES.NUTRIENT_RATIOS,
          })}
          {renderCard({
            title: "Reservoir Inflow Comparisons",
            desc: "Compare water quality between Cherry Creek and Cottonwood Creek.",
            icon: <Equalizer fontSize="large" />, // ← Matches sidebar
            to: ROUTES.WQ_INFLOW_COMPARISON,
          })}
          {renderCard({
            title: "Watershed Water Quality",
            desc: "Track nutrients & sediment at key reservoir inflow points.",
            icon: <BubbleChart fontSize="large" />, // ← Matches sidebar
            to: ROUTES.CCR_INFLOW_CONCENTRATIONS,
          })}
          {renderCard({
            title: "Water Quality Scorecard",
            desc: "Every stream site and the reservoir against benchmarks and standards, with trends, at a glance.",
            icon: <AssessmentIcon fontSize="large" />, // ← Matches sidebar
            to: ROUTES.WQ_SCORECARD,
          })}
        </Grid>
      </Section>

      {/* Explore Section */}
      <Section bg="#f7f9fb">
        <SectionHeader
          title="Explore"
          subtitle="Interactive tools for visualizing, analyzing, and comparing water quality data across time and space."
        />
        <Grid container spacing={5} justify="center">
          {renderCard({
            title: "Time Series Comparisons",
            desc: "Visualize two parameters side-by-side with full map and table support.",
            icon: <MultilineChart fontSize="large" />, // ← Matches sidebar
            to: ROUTES.TIME_SERIES_COMPARISON,
          })}
          {renderCard({
            title: "Year Comparison Graph",
            desc: "Compare seasonal water quality across multiple years for a single site.",
            icon: <Equalizer fontSize="large" />, // ← Matches sidebar
            to: ROUTES.YEAR_COMPARISON_GRAPH,
          })}
          {renderCard({
            title: "Correlation Graph",
            desc: "Explore statistical relationships between any two parameters.",
            icon: <BubbleChart fontSize="large" />, // ← Matches sidebar
            to: ROUTES.CORRELATION_GRAPH,
          })}
          {renderCard({
            title: "Precipitation in the Basin",
            desc: "Compare daily rainfall totals and cumulative patterns.",
            icon: <Timeline fontSize="large" />, // ← Matches sidebar
            to: ROUTES.PRECIPITATION,
          })}
          {renderCard({
            title: "Streamflow Data",
            desc: "Analyze daily flow and stage data from stream sensors.",
            icon: <ShowChartIcon fontSize="large" />,
            to: ROUTES.STREAMFLOW_DATA,
          })}
          {renderCard({
            title: "Upstream to Downstream",
            desc: "Follow a parameter down a stream reach, site by site, against a multi-year comparison.",
            icon: <TrendingDown fontSize="large" />,
            to: ROUTES.UPSTREAM_DOWNSTREAM,
          })}
          {renderCard({
            title: "Monthly Climate Comparison",
            desc: "Compare monthly rainfall and air temperature against the long-term average.",
            icon: <WbSunny fontSize="large" />, // ← Matches sidebar
            to: ROUTES.CLIMATE_MONTHLY,
          })}
        </Grid>
      </Section>

      {/* Tools & Reports Section */}
      <Section bg="#ffffff">
        <SectionHeader
          title="Tools & Reports"
          subtitle="Utility pages and planning documents to filter, download, and review data."
        />
        <Grid container spacing={5} justify="center">
          {renderCard({
            title: "PRF Statistics Tool",
            desc: "Launch the external Shiny dashboard to explore before/after stats.",
            icon: <VerticalSplit fontSize="large" />, // ← Matches sidebar
            to: ROUTES.PRF_STATISTICS_TOOL,
          })}
          {renderCard({
            title: "Query & Download",
            desc: "Export time-series datasets by location, date, and parameter.",
            icon: <GetAppIcon fontSize="large" />,
            to: ROUTES.DOWNLOAD,
          })}
          {renderCard({
            title: "Site Summary Table",
            desc: "Review summarized metrics by site in a sortable table.",
            icon: <TimelineIcon fontSize="large" />,
            to: ROUTES.SITE_SUMMARY_TABLE,
          })}
        </Grid>
      </Section>
      <Divider />
    </>
  );
}
